import {
    Dialog,
    useStyles,
    FilterContentLayout,
    Grid,
    Caption,
    pxToRem,
    pxArrayToRem,
    Checkbox,
    useTheme,
} from '@archipro-design/aria';
import React, { useEffect, useState } from 'react';
import * as S from './FilterDialog.mobile.style';

import { Link, useLocation } from '@remix-run/react';
import type { ElasticSearchSortOptions } from '@modules/search';
import { pluralize } from '~/utils';
import { getSortOptions } from '@modules/professional/util/filter-sort-helper';
interface FilterDialogProps {
    defaultSort?: ElasticSearchSortOptions;
    itemCount?: number;
    headerTitle?: string;
    openFilterDialog?: boolean;
    showPriceSortBy?: boolean;
    filterComponent?: React.ReactNode;
    onClose: () => void;
}

const FilterDialogMobile: React.FC<FilterDialogProps> = ({
    defaultSort = 'popular',
    itemCount = 0,
    headerTitle = 'Filters',
    openFilterDialog = false,
    showPriceSortBy = false,
    onClose,
    filterComponent,
}) => {
    const { css } = useStyles();
    const [open, setOpen] = React.useState(openFilterDialog);

    useEffect(() => {
        setOpen(openFilterDialog);
    }, [openFilterDialog]);

    const onCancelOrClose = () => {
        setOpen(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpen={() => setOpen(true)}
            onCancel={onCancelOrClose}
            content={
                <FilterDialogContent
                    defaultSort={defaultSort}
                    itemCount={itemCount}
                    headerTitle={headerTitle}
                    onClose={onCancelOrClose}
                    filterComponent={filterComponent}
                    showPriceSortBy={showPriceSortBy}
                />
            }
            design={{
                minHeight: `100vh`,
            }}
            variables={{
                rootBorderRadius: 0,
                rootWidth: '100%',
                rootPadding: 0,
                rootMaxWidth: `100vw`,
                rootMaxHeight: `100vh`,
                contentMargin: `0`,
            }}
            className={css(S.DialogContentWrapper)}
        />
    );
};

export default FilterDialogMobile;

const FilterDialogContent: React.FC<
    FilterDialogProps & { onClose: () => void }
> = ({
    defaultSort,
    itemCount,
    onClose,
    filterComponent,
    headerTitle,
    showPriceSortBy,
}) => {
    const theme = useTheme();
    const { css } = useStyles();
    const [sortParam, setSortParam] = useState(defaultSort);

    const location = useLocation();
    const searchParams = new URLSearchParams({
        page: '1',
        ...(sortParam && { sortBy: sortParam.toString() }),
    });
    const sortOptions = getSortOptions(showPriceSortBy);

    return (
        <FilterContentLayout
            onClose={onClose}
            title={headerTitle}
            submitButton={{
                children: `VIEW ${pluralize(itemCount || 0, 'RESULT')}`,
                as: Link,
                to: `${location.pathname}?${searchParams.toString()}`,
                onClick: () => onClose(),
                color: 'dark',
            }}
            closeButton={{}}
            headerAction={{
                children: 'Clear all',
                onClick: () => {
                    setSortParam(defaultSort);
                },
            }}
            content={
                <Grid columns={'100%'} className={css(S.SortFilterGrid)}>
                    <div className={css(S.SortByWrapper)}>
                        <Caption
                            variant="03"
                            design={{
                                marginBottom: pxToRem(8),
                                padding: pxArrayToRem([2, 0]),
                            }}
                        >{`SORT BY`}</Caption>
                        {sortOptions.map((s) => {
                            return (
                                <Checkbox
                                    key={s.param}
                                    checked={s.param === sortParam}
                                    onChange={() => setSortParam(s.param)}
                                    indicator={{
                                        kind: 'radio',
                                        indicator: {
                                            variables: {
                                                border: `1px solid ${theme.siteVariables.colors.charcoal[250]}`,
                                                size: pxToRem(14),
                                                checkedBackgroundColor:
                                                    theme.siteVariables.colors
                                                        .charcoal[250],
                                            },
                                        },
                                    }}
                                    label={{
                                        children: s.item,
                                        variables: {
                                            fontSize: pxToRem(16),
                                            letterSpacing: 'normal',
                                        },
                                    }}
                                    variables={{
                                        labelLineHeight: pxToRem(40),
                                        radioIndicatorLabelGap: pxToRem(6),
                                        radioIndicatorLabelCheckedGap:
                                            pxToRem(6),
                                    }}
                                />
                            );
                        })}
                    </div>

                    {filterComponent && (
                        <div className={css(S.FilterComponentWrapper)}>
                            {filterComponent}
                        </div>
                    )}
                </Grid>
            }
        />
    );
};
